import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useTenant } from "@/lib/hooks/useTenant";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { TemplatesSection } from "@/components/templates/TemplatesSection";
import { WatermarkSection } from "@/components/watermark/WatermarkSection";
import {
  Palette,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Eye,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface TenantConfig {
  cnpj?: string | null;
  address?: string | null;
  logo_url?: string | null;
  primary_color?: string | null;
  favicon_url?: string | null;
}

interface ColorOption {
  label: string;
  value: string;
  className: string;
}

const colorPalette: ColorOption[] = [
  { label: "Verde", value: "#22c55e", className: "bg-[#22c55e]" },
  { label: "Azul", value: "#3b82f6", className: "bg-[#3b82f6]" },
  { label: "Roxo", value: "#8b5cf6", className: "bg-[#8b5cf6]" },
  { label: "Laranja", value: "#f97316", className: "bg-[#f97316]" },
  { label: "Rosa", value: "#ec4899", className: "bg-[#ec4899]" },
];

const DEFAULT_COLOR = "#22c55e";

export function SettingsPersonalizacaoPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { data: tenantBilling, isLoading, isError } = useTenant();
  const tenant = tenantBilling?.tenant ?? null;

  const tenantConfig = (tenant?.config as TenantConfig | undefined) ?? {};

  const [selectedColor, setSelectedColor] = useState(DEFAULT_COLOR);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (tenant) {
      const saved = tenantConfig.primary_color;
      if (saved) {
        setSelectedColor(saved);
      }
    }
  }, [tenant?.id]);

  const handleSave = async (): Promise<void> => {
    if (!tenant) return;

    setSaving(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    const { error: updateError } = await supabase
      .from("tenants")
      .update({
        config: {
          ...tenantConfig,
          primary_color: selectedColor,
        },
      })
      .eq("id", tenant.id);

    setSaving(false);

    if (updateError) {
      setErrorMessage("Não foi possível salvar a cor de destaque.");
      return;
    }

    setSuccessMessage("Cor de destaque salva com sucesso.");
    await queryClient.invalidateQueries({ queryKey: ["tenant-billing"] });
  };

  const error =
    isError ? "Não foi possível carregar os dados do tenant." : null;

  const appliedColor = selectedColor;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <PageHeader
        title="Personalização"
        subtitle="Customize a aparência do LucraLink para sua empresa."
      />

      {error && (
        <div className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertCircle size={15} className="shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {isLoading && (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="rounded-xl border border-border bg-card overflow-hidden"
            >
              <div className="p-5 space-y-3">
                <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                <div className="h-10 w-full animate-pulse rounded bg-muted/60" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && tenant && (
        <div className="space-y-6">
          {/* Card: Cor de destaque */}
          <section
            className="rounded-xl border border-border bg-card overflow-hidden"
            aria-labelledby="section-primary-color"
          >
            <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border bg-[hsl(var(--surface-raised)/0.4)]">
              <Palette size={14} className="text-muted-foreground" />
              <h2
                id="section-primary-color"
                className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
              >
                Cor de destaque
              </h2>
            </div>
            <div className="p-5 space-y-5">
              <p className="text-sm text-muted-foreground">
                Escolha a cor principal que será usada nos botões, links e
                elementos de destaque do painel.
              </p>

              {/* Paleta de cores */}
              <div className="flex flex-wrap gap-3">
                {colorPalette.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setSelectedColor(color.value)}
                    className={cn(
                      "group relative flex h-14 w-14 items-center justify-center rounded-xl transition-all duration-200",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      selectedColor === color.value
                        ? "ring-2 ring-offset-2 ring-foreground scale-110"
                        : "hover:scale-105",
                    )}
                    style={{ backgroundColor: color.value }}
                    aria-label={`Cor ${color.label}`}
                    aria-current={selectedColor === color.value ? "true" : undefined}
                  >
                    {selectedColor === color.value && (
                      <CheckCircle2
                        size={18}
                        className="text-white drop-shadow-sm"
                      />
                    )}
                    <span className="sr-only">{color.label}</span>
                  </button>
                ))}
              </div>

              {/* Nome da cor selecionada */}
              <div className="flex items-center gap-3">
                <div
                  className="h-6 w-6 rounded-md border border-border shrink-0"
                  style={{ backgroundColor: appliedColor }}
                />
                <span className="text-sm text-muted-foreground font-mono">
                  {appliedColor.toUpperCase()}
                </span>
              </div>
            </div>
          </section>

          {/* Card: Preview ao vivo */}
          <section
            className="rounded-xl border border-border bg-card overflow-hidden"
            aria-labelledby="section-preview"
          >
            <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border bg-[hsl(var(--surface-raised)/0.4)]">
              <Eye size={14} className="text-muted-foreground" />
              <h2
                id="section-preview"
                className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
              >
                Preview
              </h2>
            </div>
            <div className="p-5">
              <div className="rounded-lg border border-border bg-surface-raised p-5 space-y-4">
                <p className="text-sm font-medium text-foreground">
                  Preview da cor de destaque
                </p>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm transition-all"
                    style={{ backgroundColor: appliedColor }}
                  >
                    Botão primário
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 rounded-md border px-4 py-2 text-sm font-medium transition-all"
                    style={{
                      borderColor: appliedColor,
                      color: appliedColor,
                    }}
                  >
                    Botão outline
                  </button>
                </div>

                <div className="space-y-2 text-sm">
                  <p>
                    Link de exemplo:{" "}
                    <span
                      className="font-medium underline underline-offset-2 cursor-default"
                      style={{ color: appliedColor }}
                    >
                      ir para o dashboard
                    </span>
                  </p>
                </div>

                <div
                  className="h-2 w-full rounded-full"
                  style={{ backgroundColor: appliedColor }}
                />
              </div>
            </div>
          </section>

          {/* Card: Logo da marca e Favicon (placeholder) */}
          <section
            className="rounded-xl border border-border bg-card overflow-hidden"
            aria-labelledby="section-branding"
          >
            <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border bg-[hsl(var(--surface-raised)/0.4)]">
              <Palette size={14} className="text-muted-foreground" />
              <h2
                id="section-branding"
                className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
              >
                Logo e Favicon
              </h2>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-muted-foreground">
                Upload de logo da marca e favicon estará disponível em breve.
                Após o upload, a logo será exibida no painel e o favicon na aba
                do navegador.
              </p>
            </div>
          </section>

          {/* Feedback messages */}
          {successMessage && (
            <div className="flex items-center gap-3 rounded-lg border border-primary/25 bg-primary/10 px-4 py-3 text-sm text-primary">
              <CheckCircle2 size={15} className="shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}

          {errorMessage && (
            <div className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              <AlertCircle size={15} className="shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Save button */}
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Palette size={15} />
              )}
              Salvar personalização
            </Button>
          </div>
        </div>
      )}

      <TemplatesSection />

      <WatermarkSection />

      {!isLoading && !tenant && !error && (
        <div className="rounded-xl border border-border bg-card p-10 text-center">
          <p className="text-sm text-muted-foreground">
            Nenhum tenant encontrado para esta conta.
          </p>
        </div>
      )}
    </div>
  );
}
